import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

import styles from './Heading.module.scss';
import type { HeadingData, HeadingLevel, HeadingProps, TextAlignment } from './Heading.types';
import { applyAccentBrackets, parseBoldTags } from './Heading.utils';

const FormatHeading = ({
  accentTreatment,
  heading,
  headingType,
  highlightColor,
  numberOfColoredWords,
  sizeDesktop,
  sizeMobile,
  titleClassName,
  variation,
}: {
  accentTreatment?: boolean;
  heading: HeadingData;
  headingType: HeadingLevel | 'display';
  highlightColor: string;
  numberOfColoredWords?: number;
  sizeDesktop?: number;
  sizeMobile?: number;
  titleClassName?: string;
  variation?: string | null;
}) => {
  const text = heading.text ?? '';

  const hasBrackets = /\[\[/.test(text);
  const normalizedText =
    !hasBrackets && numberOfColoredWords && numberOfColoredWords > 0
      ? applyAccentBrackets(text, numberOfColoredWords)
      : text;

  const parts = normalizedText.split(/\[\[|\]\]/);
  const elements: ReactNode[] = [];

  parts.forEach((part, index) => {
    if (!part) return;
    const isHighlighted = index % 2 === 1;
    const lines = part.split(/<br>|\n/);

    lines.forEach((line, lineIndex) => {
      const titleStyle: CSSProperties & Record<string, string> = {};

      if (isHighlighted) {
        titleStyle.color = highlightColor;
      }
      if (sizeMobile) {
        titleStyle['--heading-size-mobile'] = `${sizeMobile}px`;
      }
      if (sizeDesktop) {
        titleStyle['--heading-size-desktop'] = `${sizeDesktop}px`;
      }

      const hasBold = /<b>/i.test(line);

      elements.push(
        <span
          key={`${line}-${index}-${lineIndex}`}
          className={clsx(
            styles.title,
            isHighlighted && styles.highlight,
            isHighlighted && accentTreatment && styles.accentTreatment,
            variation === '140Years' && styles.variation140Years,
            variation?.toLowerCase() === 'moderat' && styles.variationModerat,
            styles[`heading${headingType.toUpperCase()}` as keyof typeof styles],
            titleClassName
          )}
          style={titleStyle}
          data-testid={`FormatHeadingTitle-${index}-${lineIndex}`}
        >
          {hasBold ? parseBoldTags(line) : line}
        </span>
      );

      if (lineIndex < lines.length - 1) {
        elements.push(<br key={`br-${line}-${index}-${lineIndex}`} />);
      }
    });
  });

  return <>{elements}</>;
};

export const Heading = ({
  Heading: heading,
  accentTreatment,
  className,
  color,
  'data-testid': dataTestId,
  formatHeadingClassName,
  isHeroSlider,
  mobileTextAlign,
  numberOfColoredWords,
  shouldApplyBottomPadding,
  sizeDesktop,
  sizeMobile,
  titleClassName,
}: HeadingProps) => {
  if (!heading?.text) {
    return null;
  }

  const headingType = (heading.headingType ?? 'h1') as HeadingLevel;
  const headingSeoTag = (heading.headingSeoTag ?? 'h2') as HeadingLevel;
  const textAlignment = (heading.textAlignment ?? 'center') as TextAlignment;
  const mainColor =
    color ?? heading.mainColor ?? 'var(--heading-default-color, var(--color-secondary))';
  const highlightColor = heading.highlightColor ?? 'var(--color-text-accent, var(--color-heading))';

  const HeadingTag = headingSeoTag;

  const wrapperStyle: CSSProperties = { color: mainColor };
  const boxStyle: CSSProperties = { color: mainColor };

  return (
    <div
      className={clsx(
        styles.heading,
        styles[
          `align${textAlignment.charAt(0).toUpperCase()}${textAlignment.slice(1)}` as keyof typeof styles
        ],
        shouldApplyBottomPadding && styles.withBottomPadding,
        isHeroSlider && styles.heroSliderMobile,
        className
      )}
      style={wrapperStyle}
      data-testid={dataTestId ?? 'renderContentfulHeadingSectionHeading'}
    >
      <HeadingTag
        className={clsx(
          styles.formatHeadingBox,
          styles[`heading${headingType.toUpperCase()}` as keyof typeof styles],
          styles[
            `align${(mobileTextAlign ?? textAlignment).charAt(0).toUpperCase()}${(mobileTextAlign ?? textAlignment).slice(1)}` as keyof typeof styles
          ],
          formatHeadingClassName
        )}
        style={boxStyle}
        data-testid="FormatHeadingBox"
      >
        <FormatHeading
          accentTreatment={accentTreatment}
          heading={heading}
          headingType={headingType}
          highlightColor={highlightColor}
          numberOfColoredWords={numberOfColoredWords}
          sizeDesktop={sizeDesktop}
          sizeMobile={sizeMobile}
          titleClassName={titleClassName}
          variation={heading.headingVariation}
        />
      </HeadingTag>
    </div>
  );
};
